import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SpeedTest } from './SpeedTest';
import { Assistant } from './Assistant';
import { GlobalSearch } from './GlobalSearch';
import { Footer } from './Footer';
import { LiveNetwork } from './LiveNetwork';
import { useLocation } from 'react-router-dom';
import { canvasSurfaceFor } from '../lib/canvas-surface';
import { AccountMenu } from './AccountMenu';
import { Nav } from './Nav';
import { PasswordAgeNotice } from './PasswordAgeNotice';
import { NotificationTray } from './NotificationTray';
import { OutboundLink } from './LeavingDialog';
import { COMPANY_SITE, COMPANY_SITE_LABEL } from '../lib/site';
import { useSession } from '../lib/session';
/**
 * Whether the rail is narrowed to icons, remembered per browser.
 *
 * A preference, not data: it belongs to whoever is sitting at this machine and
 * nobody else needs to know it, which is exactly what `localStorage` is for.
 * Wrapped because a browser with site data blocked throws on the accessor
 * itself rather than returning null — and a rail that refuses to render because
 * a preference could not be read would be a poor trade.
 */
const RAIL_KEY = 'gsn.rail.collapsed';

const readCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(RAIL_KEY) === 'yes';
  } catch {
    return false;
  }
};

/**
 * Time of day in Kisii, not in the browser's guess at a timezone. A dispatcher
 * on a laptop still set to UTC should not be told good evening at lunchtime.
 */
const nairobiHour = (): number =>
  Number(
    new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric',
      hour12: false,
      timeZone: 'Africa/Nairobi',
    }).format(new Date()),
  );
const greeting = (): string => {
  const hour = nairobiHour();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};
const today = (): string =>
  new Intl.DateTimeFormat('en-KE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Africa/Nairobi',
  }).format(new Date());
export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useSession();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  /*
   * The router's location, not the global one. `window.location` reads
   * correctly and never tells React anything changed, so the canvas would
   * keep whatever surface the first page happened to want.
   */
  const { pathname } = useLocation();

  /*
   * Remembered, so it survives a refresh. Somebody who narrowed the rail to see
   * more of a table did not mean "until I reload".
   */
  useEffect(() => {
    try {
      window.localStorage.setItem(RAIL_KEY, collapsed ? 'yes' : 'no');
    } catch {
      // Site data blocked. The rail still collapses; it just forgets.
    }
  }, [collapsed]);

  return (
    <div className="flex min-h-screen">
      {/*
        Navy carries the structure. White on #0B2B5B is 13.9:1, the strongest
        pairing in the theme — and the rail is the one surface on every page.

        `sticky top-0 h-screen` keeps it where it is while the page scrolls past.
        Before this, a long board or a hundred-row audit log scrolled the
        navigation off the top, and getting anywhere else meant scrolling all
        the way back up first — on a phone-sized laptop screen that is the most
        common single action in the application made the most tedious.

        Sticky rather than fixed, deliberately: `fixed` takes the rail out of the
        flow, so the content beside it would need a matching left margin, and
        that margin then has to be kept in step with the rail's width by hand
        forever. Sticky keeps it a flex child, so `w-64` is stated once.

        `h-screen` and not `min-h-screen` — the rail must be exactly the viewport
        so its own contents can scroll inside it when every category is
        expanded, which `min-h` would defeat by growing instead.
      */}
      {/*
        `w-16` when collapsed, and a transition on the width so the content
        beside it slides rather than jumping. Only the width animates: animating
        anything else here would drag the fibre canvas behind the rail through a
        repaint on every frame.
      */}
      <aside
        className={`sticky top-0 hidden h-screen shrink-0 flex-col overflow-hidden bg-navy-700 transition-[width] duration-200 md:flex ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        {/*
          The fibre animation, behind the rail.

          It was only on the sign-in page, where somebody sees it for four
          seconds. Here it is on every screen — but *behind the chrome*, never
          behind content, which is the only placement that does not fight the
          rest of the theme. Jua asks for high contrast in direct sunlight, and
          moving shapes behind a table of numbers is the opposite of that.

          Low opacity for the same reason. White on #0B2B5B measures 13.9:1 and
          that headroom is what makes the rail readable outdoors; the animation
          is allowed to spend a little of it and no more.

          `pointer-events-none` so it never takes a click meant for a menu item,
          and `aria-hidden` because it says nothing — it is texture, and a
          screen reader announcing a canvas would be noise.

          The component already respects `prefers-reduced-motion` and stops
          drawing entirely for anybody who has asked for that.
        */}
{/* The rail keeps its plain navy. See the note on the page ground below. */}
        {/* The logo goes to the company site, and confirms first — a dispatcher
            mid-ticket should not lose what they typed to a stray click. */}
        <div
          className={`relative z-10 flex h-16 shrink-0 items-center ${
            collapsed ? 'justify-center px-0' : 'gap-2 px-4'
          }`}
        >
          {/*
            The logo is dropped when collapsed rather than shrunk.
            At 64px there is room for one thing, and the one thing worth having
            is the control that gives the rail back — a 24px logo is unreadable
            and would be occupying the only space the toggle can sit in.
          */}
          {!collapsed && (
            <OutboundLink
              href={COMPANY_SITE}
              title={`GoSpeedNet — ${COMPANY_SITE_LABEL}`}
              className="min-w-0 flex-1 rounded-lg focus-visible:outline-none"
            >
              <img
                src="/brand/logo.png"
                alt={`GoSpeedNet — visit ${COMPANY_SITE_LABEL}`}
                className="h-9 w-auto rounded-lg bg-card p-1.5"
                width={220}
                height={80}
              />
            </OutboundLink>
          )}

          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Show the menu labels' : 'Collapse the menu to icons'}
            title={collapsed ? 'Show the menu labels' : 'Collapse the menu to icons'}
            className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg
              text-navy-200 transition-colors hover:bg-navy-600 hover:text-white
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            {collapsed ? (
              <PanelLeftOpen className="h-5 w-5" aria-hidden="true" />
            ) : (
              <PanelLeftClose className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
        {/* Above the animation, which is at the default stacking level. */}
        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          <Nav collapsed={collapsed} />
        </div>
        {/* The rail's own sign-off is gone: there is a real footer on the page
            now, and two footers on one screen is one too many. */}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between gap-3 border-b border-border bg-card px-4 md:px-6">
          <OutboundLink href={COMPANY_SITE} className="md:hidden" title={COMPANY_SITE_LABEL}>
            <img
              src="/brand/logo.png"
              alt={`GoSpeedNet — visit ${COMPANY_SITE_LABEL}`}
              className="h-7 w-auto"
            />
          </OutboundLink>
          {/*
            The account, on the left, and nothing else on this side.

            This strip used to carry a greeting, the date, the assistant, the
            bell, the name, the role, "Switch account" and "Sign out" — eight
            things above a page that then had to introduce itself underneath
            them. Seven of the eight describe the session, and a session needs
            saying once, when somebody asks. They are in the menu now.

            What is left is the one question the header was really answering —
            whose seat is this — and the two controls people press more than
            once a day.
          */}
          <div className="flex min-w-0 items-center">
            <AccountMenu
              user={user}
              greeting={greeting()}
              today={today()}
              onSwitch={() =>
                void signOut(
                  `${user?.name ?? 'You'} signed out. Whoever is next can sign in below.`,
                )
              }
              onSignOut={() => void signOut()}
            />
          </div>

          {/*
            The search, given the middle of the header.
            ------------------------------------------
            It takes the space between the account and the controls, which is
            the widest the header has, because the thing it is for — getting
            from a scrap of information to a record — is the most common reason
            somebody touches this strip at all. Hidden on a phone, where there
            is no middle: the sidebar covers the same ground in two taps and a
            half-width box that pushes the bell off the edge is worse than no
            box.
          */}
          <div className="hidden flex-1 justify-center px-4 md:flex">
            <GlobalSearch />
          </div>

          <div className="flex items-center gap-1">
            {/* Ookla's own page, in a new tab. A speed figure a customer will
                accept has to come from a measurement we do not control — see
                the component for why it is not embedded. */}
            <SpeedTest />
            {/* Every role, on every screen. It renders nothing when an admin
                has switched it off, and the API refuses as well. */}
            <Assistant />
            {/* The server filters the tray to the caller's own id and their
                own role's announcements. */}
            <NotificationTray />
          </div>
        </header>
        {/*
          A flex column with the footer last, so a short page still puts the
          footer at the bottom of the viewport while a long one puts it after
          the content. The flex-1 on main is what does that.
        */}
        <PasswordAgeNotice />

        {/*
          The live network, on the page ground.

          It was on the rail first and that was the wrong canvas: 256px wide,
          mostly menu, and masked away from every label — which left it present
          and imperceptible, which was the complaint it was meant to answer.

          Here it works, because of how Jua is built. Every piece of content in
          this application sits on an opaque white card, so the animated ground
          is *never* behind text. That means it can be drawn strongly enough to
          see without spending any of the contrast the theme asks for — measured
          at 10.76:1 for the rail, and not applicable here at all.

          Fixed rather than absolute, so it does not stretch to the height of a
          hundred-row audit log and then animate a canvas ten thousand pixels
          tall. It covers the viewport and the content scrolls over it.
        */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          {/*
            And narrowed again, for the reason it was narrowed the first time.

            The note above is right that content sits on opaque cards, so the
            canvas never costs contrast. What it does cost is attention. On a
            board or a fifty-row table it is movement in the corner of the eye
            of somebody reading, and the eye keeps going back to it — which
            reads as an unfinished background rather than as the network.

            So it animates where it is the subject and flattens to a tint
            everywhere else. `canvas-surface.ts` holds which is which, because
            that decision is editorial and belongs somewhere it can be read as
            a policy rather than inferred from forty pages.
          */}
          {canvasSurfaceFor(pathname) === 'full' ? (
            <div
              className="pointer-events-none fixed inset-0 z-0 opacity-70 motion-reduce:hidden"
              aria-hidden="true"
            >
              <LiveNetwork tone="onCanvas" />
            </div>
          ) : (
            /*
              A tint, not nothing. The ground keeps the depth the cards are
              designed to sit on — it simply stops moving.
            */
            <div
              className="pointer-events-none fixed inset-0 z-0 bg-gradient-to-br from-primary-50/40 via-transparent to-secondary-50/30"
              aria-hidden="true"
            />
          )}

          {/*
            `overflow-x-hidden` on the page, never on the content that needs to
            scroll.

            The mobile audit found /users pushing the whole document 546px wide
            on a 360px screen. A page that scrolls sideways feels broken in a
            way nothing else does — the header slides off, taps land in the
            wrong place, and every screen inherits it from one wide table.

            Wide content still scrolls: ScrollX gives each table its own
            `overflow-x-auto` box and a reachable bar. This only stops that
            overflow escaping to the document, which it should never do.
          */}
          <main className="relative z-10 min-w-0 flex-1 overflow-x-hidden p-4 md:p-6">{children}</main>
          <div className="relative z-10">
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
}
